import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../lib/supabase";

/**
 * Canvassers Audit Mode — quality + legitimacy scorecard per canvasser.
 * Spec: memory/features/canvasser-program.md (Ship 6, May 22, 2026).
 *
 * The aim (per Greg): confirm canvassers are setting good, legit appointments.
 *   • Hours, approved apps, and appts/hour over the selected window.
 *   • Minimum = 1 appt / 2 hr (0.5/hr). Below min → dismissal-review flag.
 *   • Legitimacy signals: approval rate (approved ÷ submitted) and funnel survival
 *     (% of apps that reached claim / build — junk apps die early).
 *
 * Range pills (This week / Last week / Last 30 days) drive a single date window
 * that both quality + funnel are computed over.
 *
 * Data: canvasser_quality(p_start, p_end) + canvasser_funnel(p_start, p_end) RPCs.
 */

type RangeKey = "this_week" | "last_week" | "last_30" | "last_90";
const RANGE_LABELS: Record<RangeKey, string> = {
  this_week: "This week",
  last_week: "Last week",
  last_30: "Last 30 days",
  last_90: "Last 90 days",
};

interface QualityRow {
  canvasser_id: string;
  full_name: string;
  hours: number | string;
  approved_apps: number;
  submitted_apps: number;
  rejected_apps: number;
  appts_per_hour: number | string | null;
  min_threshold: number | string;
  below_min: boolean;
  approval_rate: number | string | null;
}
interface FunnelRow {
  canvasser_id: string;
  full_name: string;
  apps: number;
  claims: number;
  builds_scheduled: number;
  builds: number;
  closed_won: number;
}

function n(v: number | string | null | undefined): number {
  return typeof v === "number" ? v : Number(v) || 0;
}
function mondayStart(d = new Date()): Date {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function rangeBounds(key: RangeKey): { start: string; end: string } {
  const now = new Date();
  if (key === "this_week") {
    return { start: mondayStart(now).toISOString(), end: now.toISOString() };
  }
  if (key === "last_week") {
    const thisMon = mondayStart(now);
    const lastMon = new Date(thisMon.getTime() - 7 * 86400000);
    return { start: lastMon.toISOString(), end: thisMon.toISOString() };
  }
  if (key === "last_90") {
    return { start: new Date(now.getTime() - 90 * 86400000).toISOString(), end: now.toISOString() };
  }
  // last_30
  return { start: new Date(now.getTime() - 30 * 86400000).toISOString(), end: now.toISOString() };
}

export default function CanvassersAuditMode() {
  const [range, setRange] = useState<RangeKey>("this_week");
  const [quality, setQuality] = useState<QualityRow[]>([]);
  const [funnel, setFunnel] = useState<FunnelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setErr(null);
        const { start, end } = rangeBounds(range);
        const [q, f] = await Promise.all([
          supabase.rpc("canvasser_quality", { p_start: start, p_end: end }),
          supabase.rpc("canvasser_funnel", { p_start: start, p_end: end }),
        ]);
        if (cancelled) return;
        if (q.error || f.error) {
          setErr((q.error || f.error)!.message);
          return;
        }
        setQuality((q.data ?? []) as QualityRow[]);
        setFunnel((f.data ?? []) as FunnelRow[]);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [range]);

  const fBy = useMemo(() => Object.fromEntries(funnel.map((r) => [r.canvasser_id, r])), [funnel]);
  const rangeLabel = RANGE_LABELS[range];

  const pills = (
    <div className="flex flex-wrap gap-1.5">
      {(Object.keys(RANGE_LABELS) as RangeKey[]).map((k) => {
        const active = k === range;
        return (
          <button
            key={k}
            onClick={() => setRange(k)}
            className={
              "rounded-full px-3 py-1 text-xs font-semibold transition " +
              (active
                ? "bg-orange-600 text-white shadow-sm"
                : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50")
            }
          >
            {RANGE_LABELS[k]}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-slate-700">Canvasser scorecards</div>
        {pills}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-500">
        Minimum <span className="font-semibold text-slate-700">1 appointment per 2 hours</span> (0.5/hr),
        measured over <span className="font-semibold text-slate-700">{rangeLabel.toLowerCase()}</span>. Below minimum → flagged for dismissal review.
        Approval rate and funnel survival are legitimacy signals — low survival means apps that die at leads/claims.
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-400">Loading canvasser scorecards…</div>
      ) : err ? (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">⚠ {err}</div>
      ) : quality.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">No canvasser activity for {rangeLabel.toLowerCase()}.</div>
      ) : (
        quality.map((q) => {
          const id = q.canvasser_id;
          const fn = fBy[id];
          const name = q.full_name ?? "Canvasser";
          const flagged = !!q.below_min;
          const apps = fn ? fn.apps : 0;
          const claimSurv = apps > 0 ? Math.round((fn!.claims / apps) * 100) : null;
          const buildSurv = apps > 0 ? Math.round((fn!.builds / apps) * 100) : null;
          const apprRate = q.approval_rate != null ? Math.round(n(q.approval_rate) * 100) : null;

          return (
            <div
              key={id}
              className={"rounded-lg border bg-white p-4 " + (flagged ? "border-red-300 ring-1 ring-red-200" : "border-slate-200")}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-slate-900">{name}</div>
                {flagged ? (
                  <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-700">
                    🚩 Below 1 appt / 2 hr — review for dismissal
                  </span>
                ) : (
                  <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                    ✓ Meeting minimum
                  </span>
                )}
              </div>

              <div className="mt-3">
                <Window title={rangeLabel} q={q} />
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <Stat label="Approval rate" value={apprRate != null ? `${apprRate}%` : "—"} hint={`approved ÷ submitted (${rangeLabel.toLowerCase()})`} />
                <Stat label="App → claim" value={claimSurv != null ? `${claimSurv}%` : "—"} hint={`${fn?.claims ?? 0} of ${apps} apps`} />
                <Stat label="App → build" value={buildSurv != null ? `${buildSurv}%` : "—"} hint={`${fn?.builds ?? 0} of ${apps} apps`} />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function Window({ title, q }: { title: string; q: QualityRow | undefined }) {
  const hours = n(q?.hours);
  const apps = q?.approved_apps ?? 0;
  const rate = q?.appts_per_hour == null ? null : n(q.appts_per_hour);
  const below = !!q?.below_min;
  return (
    <div className={"rounded-lg border p-3 " + (below ? "border-red-200 bg-red-50/40" : "border-slate-200 bg-slate-50")}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={"text-lg font-bold " + (below ? "text-red-700" : "text-slate-900")}>
          {rate == null ? "—" : rate.toFixed(2)}
        </span>
        <span className="text-xs text-slate-500">appts / hr</span>
      </div>
      <div className="mt-1 text-xs text-slate-500">
        {hours.toFixed(1)} hrs · {apps} {apps === 1 ? "app" : "apps"}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-semibold text-slate-900">{value}</div>
      {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
    </div>
  );
}
